import {setUser as SentryUser} from '@sentry/react';
import fetchFeatureFlagsRequest from 'APIs/flagrAPI/evaluationBatch';
import PageWrapper from 'components/PageWrapper';
import UnderMaintenance from 'containers/UnderMaintenance';
import {useEffect, useState} from 'react';
import {Outlet, useNavigate} from 'react-router-dom';
import {fetchUserDataRequest} from 'APIs/letrusAPI/user';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import getFeatureFlag, {
  findFeatureFlag,
  hasStoredFeatureFlags,
} from 'utils/functions/getFeatureFlags';
import PageLoading from 'components/PageLoading';
import {User, UserRole} from 'APIs/letrusAPI/user/types';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useCrossDomainLogout} from 'utils/hooks/useCrossDomainLogout';
import LogoutModal from '../LogoutModal/LogoutModal';

// list of flags to be fetched
const flagKeys = [
  'app_review_popup',
  'c5_banner',
  'ocr_tour_modal',
  'return_streak_experiment',
  'evolution_portal',
  'overloaded_review_queue_warning',
  'feedback_emocional_ao_ver_devolutiva',
  'pilot_terms_and_student_contest',
  'student_recommendations_and_alerts',
  'maintenance_mode',
  'homepage_student_alert',
  'disable_copy_paste',
  'letrus_olympics',
  'diagnostic_turma_info',
];

interface UserSession {
  user: User;
  userRoleList: UserRole[];
}

// Runs every time a authenticated route is rendered
function AuthRoute(): JSX.Element {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  useCrossDomainLogout();
  const navigate = useNavigate();
  const {trackEvent} = useTrackEvent();

  const fetchUserData = fetchUserDataRequest();
  const fetchFeatureFlags = fetchFeatureFlagsRequest();
  const hasLocalFeatureFlags = hasStoredFeatureFlags();

  useEffect(() => {
    const localUserData = getUserData();
    const hasOpenSession = localUserData.user;

    if (hasOpenSession) {
      setupUserSession({
        user: localUserData.user,
        userRoleList: localUserData.userRoleList,
      });
    } else {
      // try to verify if has authentication with API
      fetchUserData.trigger(null, {
        onSuccess: (userData) => {
          if (!userData) return;

          setupUserSession({
            user: userData.user,
            userRoleList: userData.user_roles,
          });

          trackEvent('student_login', {
            auth: {method: 'username_password'},
          });
        },
        onError: () => logoutInvalidUser(),
      });
    }
  }, []);

  function logoutInvalidUser() {
    navigate('/login');

    // hides logout modal that was trigger by the 401 while attempting to fetch user data
    window.dispatchEvent(new CustomEvent('invalidAuth', {detail: 'none'}));
  }

  function setupUserSession({user, userRoleList}: UserSession) {
    // configures sentry user
    SentryUser({id: user?.id, email: user?.email, username: user?.username});

    if (hasLocalFeatureFlags) {
      // get maintenance mode from session storage
      setIsUnderMaintenance(getFeatureFlag('maintenance_mode').isOn);
      // flags already locally available, we can stop checking auth now
      setIsCheckingAuth(false);
    } else {
      // updates feature flags if needed
      fetchFeatureFlags.trigger(
        {flagKeys, userData: {user, user_roles: userRoleList}},
        {
          onSuccess: (flags) => {
            setIsUnderMaintenance(
              !!findFeatureFlag(flags, 'maintenance_mode', 'on'),
            );
            setIsCheckingAuth(false);
          },
          onError: () => {
            setIsCheckingAuth(false);
          },
        },
      );
    }
  }

  return isCheckingAuth ? (
    <PageLoading />
  ) : (
    <>
      {isUnderMaintenance ? (
        <UnderMaintenance />
      ) : (
        <PageWrapper>
          <Outlet />
          <LogoutModal />
        </PageWrapper>
      )}
    </>
  );
}

export default AuthRoute;
